import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import FormularzMiejsc from './FormularzMiejsc'
import { normalizujMiejsca, wczytajMiejsca, zProjektu } from '../../lib/teczka/miejsca'

const STAN_ZAPISU = {
  zapisuje: 'Zapisuję w Twojej teczce…',
  zapisano: 'Zapisano w Twojej teczce.',
  blad: 'Nie udało się zapisać na koncie. Odpowiedzi są w tej przeglądarce, zapiszemy je przy następnej zmianie.',
}

/**
 * „Licznik i rozdzielnica” w oknie nad formularzem zlecenia (uwagi przeglądu G3, 2026-10-25): ten sam `FormularzMiejsc`
 * co na stronie „Moja teczka”, bez wychodzenia ze zlecenia. Teczkę i zapis podaje rodzic z jednej instancji
 * `useTeczka`, więc wiersz w formularzu od razu widzi zmiany. Przy zamknięciu zapis idzie od razu, bo migawka teczki
 * powstaje przy wystawieniu zlecenia. `zMagazynem`: typ zlecenia z magazynem (pytania o magazyn).
 */
const OknoMiejsc = ({ teczka, zapis, zapiszCzesc, zMagazynem = true, onZamknij }) => {
  const tytulId = useId()
  const zamknijRef = useRef(null)
  const ostatnie = useRef(null)
  const [miejsca, setMiejsca] = useState(() => (teczka?.miejsca ? normalizujMiejsca(teczka.miejsca) : wczytajMiejsca()))
  const podpowiedz = useMemo(() => zProjektu(teczka?.projekt), [teczka?.projekt])
  const kontekst = useMemo(() => ({ zMagazynem: Boolean(zMagazynem || podpowiedz.zMagazynem) }), [zMagazynem, podpowiedz.zMagazynem])

  const zmien = (m) => {
    ostatnie.current = m
    setMiejsca(m)
    zapiszCzesc('miejsca', normalizujMiejsca(m))
  }
  const zamknij = useCallback(() => {
    if (ostatnie.current) zapiszCzesc('miejsca', normalizujMiejsca(ostatnie.current), { odroczenie: 0 })
    onZamknij()
  }, [zapiszCzesc, onZamknij])

  useEffect(() => {
    zamknijRef.current?.focus()
    const esc = (e) => { if (e.key === 'Escape') zamknij() }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [zamknij])

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-gray-900/40 p-3 sm:items-center"
         onMouseDown={(e) => { if (e.target === e.currentTarget) zamknij() }}>
      <div role="dialog" aria-modal="true" aria-labelledby={tytulId}
           className="relative flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-gray-800">
          <div>
            <h2 id={tytulId} className="m-0 text-lg font-bold text-gray-900 dark:text-gray-100">Licznik i rozdzielnica</h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Kilka krótkich pytań do teczki. Czego nie wiesz, zaznacz: wykonawca sprawdzi to na miejscu.
            </p>
          </div>
          <button ref={zamknijRef} type="button" onClick={zamknij} aria-label="Zamknij"
                  className="-mr-2 -mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800">
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          <FormularzMiejsc miejsca={miejsca} onZmiana={zmien} kontekst={kontekst} podpowiedz={podpowiedz} />
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-3 dark:border-gray-800">
          <p className="m-0 text-xs text-gray-500" aria-live="polite">{STAN_ZAPISU[zapis?.stan] || 'Odpowiedzi zapisują się w Twojej teczce.'}</p>
          <button type="button" onClick={zamknij}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500">
            Gotowe
          </button>
        </div>
      </div>
    </div>
  )
}

export default OknoMiejsc
